import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AssignableMemberDto,
  ConversationListContactStateName,
  ConversationListSortName,
  ConversationListStatusName,
  ConversationListViewName,
  OrganizationMemberRoleName,
  PlatformTypeName,
} from "@multi-chat/shared";
import {
  DEFAULT_CONVERSATION_LIST_SORT,
  DEFAULT_CONVERSATION_LIST_STATUS,
  DEFAULT_CONVERSATION_LIST_VIEW,
} from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getFacebookConnection } from "../../../api/facebook-api.js";
import { getInstagramConnection } from "../../../api/instagram-api.js";
import { getConnection } from "../../../api/whatsapp-api.js";
import { ErrorState } from "../../../components/common/ErrorState/index.js";
import { LoadingState } from "../../../components/common/LoadingState/index.js";
import { useElementWidthBucket } from "../../../components/layout/use-element-width-bucket.js";
import { useMinWidth } from "../../../components/layout/use-min-width.js";
import { motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { downloadConversationExport, listAssignableMembers } from "../../../api/inbox-api.js";
import { useOrganizations, useSelectedPlatformAdminAccess } from "../../organizations/organization-context.js";
import { useWhatsAppWorkspacePhone } from "../../whatsapp/whatsapp-workspace-phone-context.js";
import { ConversationAssigneeControl } from "../ConversationAssigneeControl.js";
import { ContactLookupDialog } from "../ContactLookupDialog.js";
import { inboxWorkflowErrorMessage } from "../inbox-workflow-errors.js";
import { ConversationDetailsPanel } from "../ConversationDetails/ConversationDetailsPanel.js";
import {
  conversationConnectedAccountName,
  type InboxChannelConnections,
} from "../conversation-connected-account.js";
import { ConversationDraftProvider, useConversationDraftStore } from "../conversation-draft-context.js";
import { commitConversationDraftOnLeave } from "../conversation-draft-store.js";
import { ConversationList } from "../ConversationList/ConversationList.js";
import { ConversationListHeader } from "../ConversationList/ConversationListHeader.js";
import { ConversationListStrip } from "../ConversationList/ConversationListStrip.js";
import { ConversationRowSkeleton } from "../ConversationList/ConversationRowSkeleton.js";
import { MobileFiltersSheet } from "../ConversationList/MobileFiltersSheet.js";
import type { MobileFilterSortDraft } from "../conversation-list-filters.js";
import { selectedComposerDetails } from "../composer-capability.js";
import {
  ConversationThread,
  type ConversationContactActions,
} from "../ConversationThread/ConversationThread.js";
import { InboxListEmpty, InboxNoResults } from "../InboxEmptyStates.js";
import {
  decideInboxConversationDeepLink,
  shouldConsumeInboxConversationDeepLink,
  withoutInboxConversationDeepLink,
} from "../inbox-conversation-deep-link.js";
import {
  INBOX_DETAILS_DOCKED_WIDTH_PX,
  INBOX_DETAILS_DRAWER_WIDTH_PX,
  INBOX_LIST_COLLAPSED_WIDTH_PX,
  INBOX_PANE_GAP_PX,
  inboxContainerSize,
  inboxLayoutMode,
  inboxListExpandedWidthPx,
  showThreadDetailsOpener,
  type InboxLayoutMode,
} from "../inbox-layout.js";
import {
  initialMobileInboxPane,
  MOBILE_INBOX_BREAKPOINT_PX,
  reduceMobileInboxPane,
  type MobileInboxPane,
} from "../mobile-inbox-nav.js";
import type { TemplateDrawerFrame } from "../template-picker.js";
import { useInbox } from "../use-inbox.js";
import { useSavedContactDetail } from "../use-saved-contact-detail.js";
const INBOX_CARD =
  "min-h-0 overflow-hidden rounded-[14px] border border-inbox-line bg-surface shadow-inbox-card";
const MOBILE_PANE = "absolute inset-0 w-auto rounded-none border-0 shadow-none";
const MOBILE_PANE_OFFSET_PX = 28;

const paneTransition = { duration: 0.3, ease: [0.2, 0.8, 0.2, 1] as const };
const mobilePaneTransition = { duration: 0.28, ease: [0.2, 0.8, 0.2, 1] as const };

export function InboxPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { selected } = useOrganizations();

  if (!selected) {
    return <LoadingState message={t("app.loading")} />;
  }

  return (
    <ConversationDraftProvider key={selected.id}>
      <InboxPageContent organizationId={selected.id} role={selected.role} />
    </ConversationDraftProvider>
  );
}

function InboxPageContent({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { inboxScopeVersion } = useWhatsAppWorkspacePhone();
  const reduceMotion = useReducedMotion();
  const draftStore = useConversationDraftStore();
  const isRtl = i18n.dir() === "rtl";
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [committedSearch, setCommittedSearch] = useState<string | undefined>();
  const [platform, setPlatform] = useState<PlatformTypeName | undefined>();
  const [unread, setUnread] = useState(false);
  const [contactState, setContactState] = useState<
    ConversationListContactStateName | undefined
  >();
  const [sort, setSort] = useState<ConversationListSortName>(DEFAULT_CONVERSATION_LIST_SORT);
  const [listStatus, setListStatus] = useState<ConversationListStatusName>(DEFAULT_CONVERSATION_LIST_STATUS);
  const [view, setView] = useState<ConversationListViewName>(DEFAULT_CONVERSATION_LIST_VIEW);
  const [assignableMembers, setAssignableMembers] = useState<AssignableMemberDto[]>([]);
  const platformAdminAccess = useSelectedPlatformAdminAccess();
  const viewerMemberId = assignableMembers.find((member) => member.isCurrentMember)?.memberId ?? null;
  const showMineView = !platformAdminAccess || viewerMemberId != null;
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const canManageContacts = role === "OWNER" || role === "ADMIN";
  const {
    listView,
    messagesView,
    selectedConversationId,
    selectedConversation,
    contactActionStatus,
    sendStatus,
    conversationDetails,
    conversationEvents,
    hasMoreConversations,
    loadingMoreConversations,
    listRefreshing,
    hasOlderMessages,
    loadingOlderMessages,
    selectConversation,
    openConversationById,
    loadMoreConversations,
    loadOlderMessages,
    ensureQuotedMessageLoaded,
    refreshList,
    refreshMessages,
    saveContact,
    restoreContact,
    clearContactActionError,
    clearSendError,
    sendMessage,
    sendTemplateMessage,
    workflowPending,
    assignConversation,
    setConversationStatus,
    markConversationUnread,
  } = useInbox(organizationId, t("inbox.unreachable"), {
    search: committedSearch,
    platform,
    unread,
    contactState,
    sort,
    view,
    status: listStatus,
  }, inboxScopeVersion, viewerMemberId);

  useEffect(() => {
    if (!showMineView && view === "MINE") setView("ALL");
  }, [showMineView, view]);

  useEffect(() => {
    const controller = new AbortController();
    void listAssignableMembers(organizationId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setAssignableMembers(response.items);
      })
      .catch(() => {
        if (!controller.signal.aborted) setAssignableMembers([]);
      });
    return () => controller.abort();
  }, [organizationId]);

  const exportConversation = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!selectedConversation) return { ok: false as const };
      try {
        await downloadConversationExport(
          organizationId,
          selectedConversation.conversationId,
          format,
          i18n.language.startsWith("he") ? "he" : "en",
        );
        return { ok: true as const };
      } catch (error) {
        return {
          ok: false as const,
          message: inboxWorkflowErrorMessage(t, error, t("inbox.unreachable")),
        };
      }
    },
    [i18n.language, organizationId, selectedConversation, t],
  );

  const [canSendTemplate, setCanSendTemplate] = useState(false);
  const [channelConnections, setChannelConnections] = useState<InboxChannelConnections | null>(null);
  /**
   * Session-only state; persisting it needs an approved storage key. Docked Details starts
   * open and remembers a manual collapse; the drawer always starts closed.
   */
  const [dockedDetailsOpen, setDockedDetailsOpen] = useState(true);
  const [drawerDetailsOpen, setDrawerDetailsOpen] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [mobilePane, setMobilePane] = useState<MobileInboxPane>(initialMobileInboxPane);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkFailedKeyRef = useRef<string | null>(null);
  const deepLinkInFlightKeyRef = useRef<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listCollapseRef = useRef<HTMLButtonElement | null>(null);
  const listExpandRef = useRef<HTMLButtonElement | null>(null);
  const detailsOpenerRef = useRef<HTMLButtonElement | null>(null);
  const detailsCloseRef = useRef<HTMLButtonElement | null>(null);
  const contactActionsRef = useRef<ConversationContactActions | null>(null);
  const [containerRef, containerSize] = useElementWidthBucket<HTMLDivElement, ReturnType<typeof inboxContainerSize>>(
    inboxContainerSize,
  );
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [threadCardEl, setThreadCardEl] = useState<HTMLDivElement | null>(null);
  const [detailsCardEl, setDetailsCardEl] = useState<HTMLDivElement | null>(null);
  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef(node);
      setRootEl(node);
    },
    [containerRef],
  );
  const isTabletOrLarger = useMinWidth(MOBILE_INBOX_BREAKPOINT_PX);
  const isMobileInbox = !isTabletOrLarger;
  const layoutMode = inboxLayoutMode({ containerSize, mobileViewport: isMobileInbox });
  const [previousLayoutMode, setPreviousLayoutMode] = useState<InboxLayoutMode>(layoutMode);
  if (previousLayoutMode !== layoutMode) {
    setPreviousLayoutMode(layoutMode);
    if (layoutMode !== "drawer") setDrawerDetailsOpen(false);
    if (layoutMode !== "mobile" && mobilePane === "details") setMobilePane("conversation");
  }

  const detailsOpen = layoutMode === "docked" ? dockedDetailsOpen : drawerDetailsOpen;
  const setDetailsOpen = layoutMode === "docked" ? setDockedDetailsOpen : setDrawerDetailsOpen;
  const hasSelection = selectedConversation != null;
  const detailsVisible =
    hasSelection && (layoutMode === "mobile" ? mobilePane === "details" : detailsOpen);
  const drawerOpen = layoutMode === "drawer" && detailsVisible;
  const dockedDetailsEl = layoutMode === "docked" && detailsVisible ? detailsCardEl : null;
  const templateDrawerFrame = useMemo<TemplateDrawerFrame | null>(
    () =>
      layoutMode !== "mobile" && rootEl && threadCardEl
        ? { host: rootEl, thread: threadCardEl, details: dockedDetailsEl }
        : null,
    [layoutMode, rootEl, threadCardEl, dockedDetailsEl],
  );
  const listCollapsedNow = listCollapsed && layoutMode !== "mobile";
  const listExpandedWidth = inboxListExpandedWidthPx(containerSize);
  const customerServiceWindowExpiresAt =
    selectedConversation == null
      ? undefined
      : conversationDetails == null
        ? undefined
        : conversationDetails.customerServiceWindowExpiresAt;
  const composerDetails = selectedComposerDetails(
    selectedConversation?.conversationId ?? null,
    conversationDetails,
  );

  useEffect(() => {
    const controller = new AbortController();
    const currentOrgId = organizationId;
    setCanSendTemplate(false);
    setChannelConnections(null);
    void (async () => {
      const [whatsapp, facebook, instagram] = await Promise.allSettled([
        getConnection(currentOrgId, controller.signal),
        getFacebookConnection(currentOrgId, controller.signal),
        getInstagramConnection(currentOrgId, controller.signal),
      ]);
      if (controller.signal.aborted) return;
      setCanSendTemplate(whatsapp.status === "fulfilled" && whatsapp.value.state === "CONNECTED");
      setChannelConnections({
        whatsapp: whatsapp.status === "fulfilled" ? whatsapp.value : null,
        facebook: facebook.status === "fulfilled" ? facebook.value : null,
        instagram: instagram.status === "fulfilled" ? instagram.value : null,
      });
    })();
    return () => controller.abort();
  }, [organizationId]);

  const selectedProfile = selectedConversation?.contactPlatformProfile;
  const savedContactDetail = useSavedContactDetail(
    organizationId,
    selectedProfile?.contactState === "ACTIVE" ? (selectedProfile.contact?.id ?? null) : null,
  );

  const connectedAccountName =
    selectedConversation == null || channelConnections == null
      ? undefined
      : conversationConnectedAccountName(
          channelConnections,
          selectedConversation.platformType,
          selectedConversation.platformEndpointId,
        );

  const handleCommittedSearchChange = useCallback((search: string | undefined) => {
    setCommittedSearch(search);
  }, []);

  function handleClearSearch(): void {
    setSearchResetKey((key) => key + 1);
    setCommittedSearch(undefined);
  }

  function handleClearAllFilters(): void {
    setPlatform(undefined);
    setUnread(false);
    setContactState(undefined);
    setListStatus(DEFAULT_CONVERSATION_LIST_STATUS);
    setSort(DEFAULT_CONVERSATION_LIST_SORT);
  }

  function handleResetFilters(): void {
    handleClearAllFilters();
    if (!searchOpen) handleClearSearch();
  }

  function handleOpenConversation(conversationId: number): void {
    setLookupOpen(false);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("conversation", String(conversationId));
      return next;
    });
  }

  function applyFilterSort(draft: MobileFilterSortDraft): void {
    setPlatform(draft.platform);
    setUnread(draft.unread);
    setContactState(draft.contactState);
    setListStatus(draft.status);
    setSort(draft.sort);
  }

  function handleSelectConversation(conversationId: number): void {
    commitConversationDraftOnLeave(draftStore, selectedConversationId, conversationId);
    selectConversation(conversationId);
    if (isMobileInbox) {
      setMobilePane(reduceMobileInboxPane(mobilePane, { type: "select_conversation" }));
    }
  }

  useEffect(() => {
    const decision = decideInboxConversationDeepLink({
      rawParam: searchParams.get("conversation"),
      organizationId,
      selectedConversationId,
      failedKey: deepLinkFailedKeyRef.current,
      inFlightKey: deepLinkInFlightKeyRef.current,
    });
    if (shouldConsumeInboxConversationDeepLink(decision)) {
      setSearchParams(withoutInboxConversationDeepLink, { replace: true });
    }

    switch (decision.action) {
      case "clear":
        deepLinkFailedKeyRef.current = null;
        deepLinkInFlightKeyRef.current = null;
        return;
      case "invalid":
      case "skip_failed":
      case "skip_in_flight":
      case "already_selected":
        return;
      case "open": {
        deepLinkInFlightKeyRef.current = decision.key;
        void openConversationById(decision.conversationId).then((result) => {
          if (deepLinkInFlightKeyRef.current === decision.key) {
            deepLinkInFlightKeyRef.current = null;
          }
          if (!result.ok) {
            deepLinkFailedKeyRef.current = decision.key;
            return;
          }
          deepLinkFailedKeyRef.current = null;
          if (!isTabletOrLarger) {
            setMobilePane((pane) =>
              reduceMobileInboxPane(pane, { type: "select_conversation" }),
            );
          }
        });
        return;
      }
      default: {
        const _exhaustive: never = decision;
        return _exhaustive;
      }
    }
  }, [
    organizationId,
    searchParams,
    setSearchParams,
    openConversationById,
    isTabletOrLarger,
    selectedConversationId,
  ]);

  function handleMobileBack(): void {
    setMobilePane(reduceMobileInboxPane(mobilePane, { type: "back_to_list" }));
    const selectedId = selectedConversationId;
    if (selectedId == null) return;
    requestAnimationFrame(() => {
      const row = document.querySelector<HTMLElement>(
        `[data-conversation-id="${selectedId}"]`,
      );
      row?.focus({ preventScroll: true });
    });
  }

  function handleOpenDetails(): void {
    if (layoutMode === "mobile") {
      setMobilePane((pane) => reduceMobileInboxPane(pane, { type: "open_details" }));
    } else {
      setDetailsOpen(true);
    }
    requestAnimationFrame(() => detailsCloseRef.current?.focus({ preventScroll: true }));
  }

  function handleCloseDetails(): void {
    if (layoutMode === "mobile") {
      setMobilePane((pane) => reduceMobileInboxPane(pane, { type: "back_to_conversation" }));
    } else {
      setDetailsOpen(false);
    }
    requestAnimationFrame(() => detailsOpenerRef.current?.focus({ preventScroll: true }));
  }

  function handleDetailsKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (!drawerOpen || event.key !== "Escape" || event.defaultPrevented) return;
    event.preventDefault();
    handleCloseDetails();
  }

  function setListCollapsedWithFocus(collapsed: boolean): void {
    setListCollapsed(collapsed);
    requestAnimationFrame(() => {
      (collapsed ? listExpandRef : listCollapseRef).current?.focus({ preventScroll: true });
    });
  }

  function handleStripSearch(): void {
    setListCollapsed(false);
    setSearchOpen(true);
    requestAnimationFrame(() => searchInputRef.current?.focus({ preventScroll: true }));
  }

  const paneForward = isRtl ? -MOBILE_PANE_OFFSET_PX : MOBILE_PANE_OFFSET_PX;
  const paneBackward = -paneForward;
  const showMobileList = mobilePane === "list";
  const showMobileConversation = mobilePane === "conversation";
  const listHidden = isMobileInbox && !showMobileList;
  const threadHidden = isMobileInbox && !showMobileConversation;
  const threadTransition = reduceMotion ? { duration: 0 } : mobilePaneTransition;

  const listAnimate = isMobileInbox
    ? { width: "auto", opacity: showMobileList ? 1 : 0, x: showMobileList || reduceMotion ? 0 : paneBackward }
    : { width: listCollapsedNow ? INBOX_LIST_COLLAPSED_WIDTH_PX : listExpandedWidth, opacity: 1, x: 0 };

  const threadAnimate = isMobileInbox
    ? {
        opacity: showMobileConversation ? 1 : 0,
        x:
          showMobileConversation || reduceMotion
            ? 0
            : mobilePane === "details"
              ? paneBackward
              : paneForward,
      }
    : { opacity: 1, x: 0 };

  function detailsAnimate(): Record<string, number | string> {
    switch (layoutMode) {
      case "docked":
        return {
          width: detailsVisible ? INBOX_DETAILS_DOCKED_WIDTH_PX : 0,
          marginInlineStart: detailsVisible ? 0 : -INBOX_PANE_GAP_PX,
          opacity: detailsVisible ? 1 : 0,
          x: 0,
        };
      case "drawer":
        return {
          width: INBOX_DETAILS_DRAWER_WIDTH_PX,
          marginInlineStart: 0,
          opacity: 1,
          x: detailsVisible || reduceMotion ? 0 : isRtl ? "-110%" : "110%",
        };
      case "mobile":
        return {
          width: "auto",
          marginInlineStart: 0,
          opacity: detailsVisible ? 1 : 0,
          x: detailsVisible || reduceMotion ? 0 : paneForward,
        };
      default: {
        const exhaustive: never = layoutMode;
        return exhaustive;
      }
    }
  }

  const messagesListContent = (
    <>
      <ConversationListHeader
        isMobile={isMobileInbox}
        showSubtitle={!isMobileInbox && containerSize === "wide"}
        searchOpen={searchOpen}
        onSearchOpenChange={setSearchOpen}
        searchResetKey={searchResetKey}
        committedSearch={committedSearch}
        searching={listRefreshing}
        onSearchChange={handleCommittedSearchChange}
        onClearSearch={handleClearSearch}
        searchInputRef={searchInputRef}
        filters={{ platform, unread, contactState, status: listStatus, sort }}
        onFiltersChange={applyFilterSort}
        onResetFilters={handleResetFilters}
        onOpenMobileFilters={() => setMobileFiltersOpen(true)}
        onOpenLookup={() => setLookupOpen(true)}
        onCollapseList={() => setListCollapsedWithFocus(true)}
        collapseButtonRef={listCollapseRef}
        view={view}
        onViewChange={setView}
        showMineView={showMineView}
      />

      {listView.kind === "loading" ? (
        <ConversationRowSkeleton label={t("inbox.loadingConversations")} />
      ) : null}

      {listView.kind === "error" ? (
        <div className="grid min-h-0 flex-1 place-items-center overflow-hidden bg-inbox-rows p-5">
          <ErrorState
            layout="panel"
            message={listView.message}
            onRetry={refreshList}
            retryLabel={t("inbox.retry")}
          />
        </div>
      ) : null}

      {listView.kind === "empty" ? <InboxListEmpty /> : null}

      {listView.kind === "searchEmpty" ? (
        <InboxNoResults actionLabel={t("inbox.search.emptyClear")} onAction={handleClearSearch} />
      ) : null}

      {listView.kind === "filterEmpty" || listView.kind === "searchFilterEmpty" ? (
        <InboxNoResults
          actionLabel={t("inbox.noResults.clearFilters")}
          onAction={handleClearAllFilters}
        />
      ) : null}

      {listView.kind === "ready" ? (
        <ConversationList
          items={listView.items}
          selectedConversationId={selectedConversationId}
          hasMore={hasMoreConversations}
          loadingMore={loadingMoreConversations}
          refreshing={listRefreshing}
          sort={sort}
          onSelect={handleSelectConversation}
          onLoadMore={loadMoreConversations}
        />
      ) : null}
    </>
  );

  return (
    <div
      ref={setRootRef}
      className={cn(
        "font-feedbot relative flex min-h-0 min-w-0 w-full flex-1 overflow-clip bg-canvas text-inbox-text",
        isMobileInbox ? "pb-[env(safe-area-inset-bottom,0px)]" : "gap-2 p-2",
      )}
      data-inbox-layout={layoutMode}
    >
      <motion.aside
        className={cn(
          INBOX_CARD,
          "relative flex shrink-0 flex-col",
          isMobileInbox && cn(MOBILE_PANE, "z-[1]"),
        )}
        aria-label={t("inbox.listLabel")}
        aria-hidden={listHidden || undefined}
        inert={listHidden || undefined}
        initial={false}
        animate={listAnimate}
        transition={reduceMotion ? { duration: 0 } : isMobileInbox ? mobilePaneTransition : paneTransition}
        style={{
          pointerEvents: listHidden ? "none" : "auto",
          visibility: listHidden ? "hidden" : "visible",
        }}
      >
        <motion.div
          className={cn(
            "flex h-full min-h-0 flex-col",
            isMobileInbox ? "w-full" : containerSize === "narrow" ? "w-[286px]" : "w-[318px]",
          )}
          inert={listCollapsedNow || undefined}
          aria-hidden={listCollapsedNow || undefined}
          initial={false}
          animate={{ opacity: listCollapsedNow ? 0 : 1 }}
          transition={reduceMotion ? { duration: 0 } : { duration: listCollapsedNow ? 0.14 : 0.2, delay: listCollapsedNow ? 0 : 0.06 }}
        >
          {messagesListContent}
        </motion.div>
        {!isMobileInbox ? (
          <motion.div
            className="absolute inset-y-0 start-0"
            inert={!listCollapsedNow || undefined}
            aria-hidden={!listCollapsedNow || undefined}
            initial={false}
            animate={{ opacity: listCollapsedNow ? 1 : 0 }}
            transition={reduceMotion ? { duration: 0 } : { duration: listCollapsedNow ? 0.22 : 0.14, delay: listCollapsedNow ? 0.12 : 0 }}
            style={{ pointerEvents: listCollapsedNow ? "auto" : "none" }}
          >
            <ConversationListStrip
              items={listView.kind === "ready" ? listView.items : []}
              selectedConversationId={selectedConversationId}
              sort={sort}
              searchActive={committedSearch != null}
              onSelect={handleSelectConversation}
              onExpand={() => setListCollapsedWithFocus(false)}
              onSearch={handleStripSearch}
              expandButtonRef={listExpandRef}
            />
          </motion.div>
        ) : null}
      </motion.aside>

      <motion.div
        ref={setThreadCardEl}
        className={cn(
          INBOX_CARD,
          "relative flex min-w-0 flex-col bg-inbox-thread",
          isMobileInbox ? cn(MOBILE_PANE, "z-[2]") : "flex-1",
        )}
        aria-hidden={threadHidden || undefined}
        inert={threadHidden || undefined}
        initial={false}
        animate={threadAnimate}
        transition={threadTransition}
        style={{
          pointerEvents: threadHidden ? "none" : "auto",
          visibility: threadHidden ? "hidden" : "visible",
        }}
      >
        <ConversationThread
          conversation={selectedConversation}
          organizationId={organizationId}
          messagesView={messagesView}
          conversationEvents={conversationEvents}
          contactActionStatus={contactActionStatus}
          sendStatus={sendStatus}
          composerCapabilityKnown={composerDetails != null}
          customerServiceWindowExpiresAt={composerDetails?.customerServiceWindowExpiresAt ?? null}
          canSendFreeformText={composerDetails?.canSendFreeformText ?? false}
          freeformSendBlockedReason={composerDetails?.freeformSendBlockedReason ?? null}
          lastInboundCustomerMessageAt={
            conversationDetails?.lastInboundCustomerMessageAt ?? null
          }
          canSendTemplate={canSendTemplate}
          connectedAccountName={connectedAccountName ?? null}
          hasOlderMessages={hasOlderMessages}
          loadingOlderMessages={loadingOlderMessages}
          onRetry={refreshMessages}
          onLoadOlderMessages={loadOlderMessages}
          ensureQuotedMessageLoaded={ensureQuotedMessageLoaded}
          onSaveContact={saveContact}
          onRestoreContact={restoreContact}
          onClearContactActionError={clearContactActionError}
          onClearSendError={clearSendError}
          onSendMessage={sendMessage}
          onSendTemplateMessage={sendTemplateMessage}
          assignableMembers={assignableMembers}
          workflowPending={workflowPending}
          onAssign={(memberId) =>
            selectedConversation
              ? assignConversation(selectedConversation.conversationId, memberId)
              : Promise.resolve({ ok: false })
          }
          onSetStatus={(status) =>
            selectedConversation
              ? setConversationStatus(selectedConversation.conversationId, status)
              : Promise.resolve({ ok: false })
          }
          onMarkUnread={() =>
            selectedConversation
              ? markConversationUnread(selectedConversation.conversationId)
              : Promise.resolve({ ok: false })
          }
          onExport={exportConversation}
          inboxPlaceholder={listView.kind === "empty" ? "zero" : "select"}
          contactActionsRef={contactActionsRef}
          templateDrawerFrame={templateDrawerFrame}
          showDetailsTrigger={showThreadDetailsOpener(layoutMode, detailsVisible)}
          detailsExpanded={detailsVisible}
          onOpenDetails={handleOpenDetails}
          detailsTriggerRef={detailsOpenerRef}
          showMobileBack={isMobileInbox && mobilePane !== "list"}
          onMobileBack={handleMobileBack}
        />
      </motion.div>

      {hasSelection ? (
        <motion.div
          ref={setDetailsCardEl}
          id="inbox-details"
          className={cn(
            "flex min-h-0 shrink-0 flex-col",
            layoutMode === "drawer" &&
              "absolute inset-y-2 end-2 z-30 max-w-[92%] overflow-hidden rounded-[14px] shadow-inbox-pop",
            layoutMode === "docked" && INBOX_CARD,
            layoutMode === "mobile" && "absolute inset-0 z-[3]",
          )}
          role={drawerOpen ? "dialog" : undefined}
          aria-label={
            drawerOpen ? t("inbox.details.panelLabel") : undefined
          }
          aria-hidden={!detailsVisible || undefined}
          inert={!detailsVisible || undefined}
          initial={false}
          animate={detailsAnimate()}
          transition={reduceMotion ? { duration: 0 } : layoutMode === "mobile" ? mobilePaneTransition : paneTransition}
          style={{ pointerEvents: detailsVisible ? "auto" : "none" }}
          onKeyDown={handleDetailsKeyDown}
        >
          {selectedConversation ? (
            <ConversationDetailsPanel
              conversation={selectedConversation}
              customerServiceWindowExpiresAt={customerServiceWindowExpiresAt}
              lastInboundCustomerMessageAt={composerDetails?.lastInboundCustomerMessageAt ?? null}
              metaWindowOpen={
                composerDetails == null || selectedConversation.platformType === "WHATSAPP"
                  ? undefined
                  : composerDetails.freeformSendBlockedReason !== "WINDOW_CLOSED"
              }
              connectedAccountName={connectedAccountName}
              contactDetail={savedContactDetail.state}
              onRetryContactDetail={savedContactDetail.retry}
              onOpenConversation={handleOpenConversation}
              canManageContacts={canManageContacts}
              contactActionPending={contactActionStatus.kind === "submitting"}
              originatingSocialComment={conversationDetails?.originatingSocialComment ?? null}
              onSaveContact={() => contactActionsRef.current?.openSave()}
              onRestoreContact={() => contactActionsRef.current?.openRestore()}
              assigneeControl={
                <ConversationAssigneeControl
                  assignedMember={selectedConversation.assignedMember}
                  members={assignableMembers}
                  pending={workflowPending}
                  onAssign={(memberId) =>
                    assignConversation(selectedConversation.conversationId, memberId)
                  }
                />
              }
              closeControl={layoutMode === "mobile" ? "back" : "collapse"}
              onClose={handleCloseDetails}
              closeButtonRef={detailsCloseRef}
              className={cn(
                layoutMode === "docked" && "w-[298px]",
                layoutMode === "drawer" && "rounded-[14px] border border-inbox-line",
                layoutMode === "mobile" && "rounded-none",
              )}
            />
          ) : null}
        </motion.div>
      ) : null}

      {drawerOpen ? (
        <motion.div
          className="absolute inset-0 z-[25] bg-[rgb(6_29_26/0.22)]"
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={reduceMotion ? { duration: 0 } : { duration: 0.2 }}
          onClick={handleCloseDetails}
        />
      ) : null}

      <ContactLookupDialog
        open={lookupOpen}
        onOpenChange={setLookupOpen}
        organizationId={organizationId}
        onOpenConversation={handleOpenConversation}
      />

      {isMobileInbox ? (
        <MobileFiltersSheet
          open={mobileFiltersOpen}
          onOpenChange={setMobileFiltersOpen}
          platform={platform}
          unread={unread}
          contactState={contactState}
          status={listStatus}
          sort={sort}
          onApply={applyFilterSort}
        />
      ) : null}
    </div>
  );
}